import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { defaultSettings, seedProjects } from "../domain/seed";
import type { Project, ProjectMedia, SiteSettings } from "../domain/schemas";

interface MediaRecord extends ProjectMedia { blob: Blob }
interface PortfolioDb extends DBSchema {
  projects: { key: string; value: Project; indexes: { "by-slug": string; "by-order": number } };
  media: { key: string; value: MediaRecord; indexes: { "by-project": string } };
  settings: { key: string; value: SiteSettings };
  meta: { key: string; value: { key: string; value: string } };
}

let connection: Promise<IDBPDatabase<PortfolioDb>> | null = null;
const CURRENT_SEED_VERSION = "3";

export function getDatabase() {
  if (!connection) {
    connection = openDB<PortfolioDb>("yuri-portfolio", 1, {
      upgrade(db) {
        const projects = db.createObjectStore("projects", { keyPath: "id" });
        projects.createIndex("by-slug", "slug", { unique: true });
        projects.createIndex("by-order", "sortOrder");
        const media = db.createObjectStore("media", { keyPath: "id" });
        media.createIndex("by-project", "projectId");
        db.createObjectStore("settings", { keyPath: "id" });
        db.createObjectStore("meta", { keyPath: "key" });
      },
    });
  }
  return connection;
}

export async function ensureSeeded() {
  const db = await getDatabase();
  const version = await db.get("meta", "seed-version");
  if (version?.value === CURRENT_SEED_VERSION) return;
  const tx = db.transaction(["projects", "settings", "meta"], "readwrite");
  const projectStore = tx.objectStore("projects");
  for (const project of seedProjects) {
    const existing = await projectStore.get(project.id);
    if (!existing) await projectStore.put(project);
    else if (!existing.category) await projectStore.put({ ...existing, category: project.category, maturity: project.maturity, access: project.access, coverImage: project.coverImage });
  }
  const settingsStore = tx.objectStore("settings");
  const settings = await settingsStore.get("primary");
  if (!settings) await settingsStore.put(defaultSettings);
  else if (settings.headline === "Engenharia de software para a complexidade do mundo real.") {
    await settingsStore.put({ ...settings, headline: defaultSettings.headline, bio: defaultSettings.bio });
  }
  // Corrige apenas o contato antigo do seed, preservando personalizações locais.
  const savedSettings = await settingsStore.get("primary");
  if (savedSettings?.email === "yurralmeida@gmail.com") {
    await settingsStore.put({ ...savedSettings, email: defaultSettings.email });
  }
  await tx.objectStore("meta").put({ key: "seeded", value: "1" });
  await tx.objectStore("meta").put({ key: "seed-version", value: CURRENT_SEED_VERSION });
  await tx.done;
}

export const portfolioRepository = {
  async listProjects(includeDeleted = false) {
    const rows = await (await getDatabase()).getAllFromIndex("projects", "by-order");
    return includeDeleted ? rows : rows.filter((row) => !row.deletedAt);
  },
  async getProject(id: string) { return (await getDatabase()).get("projects", id); },
  async getBySlug(slug: string) { return (await getDatabase()).getFromIndex("projects", "by-slug", slug); },
  async saveProject(project: Project) { await (await getDatabase()).put("projects", project); },
  async saveProjects(projects: Project[]) {
    const tx = (await getDatabase()).transaction("projects", "readwrite");
    for (const project of projects) await tx.store.put(project);
    await tx.done;
  },
  async trash(id: string) {
    const project = await this.getProject(id); if (!project) return;
    await this.saveProject({ ...project, deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
  },
  async restore(id: string) {
    const project = await this.getProject(id); if (!project) return;
    await this.saveProject({ ...project, deletedAt: null, updatedAt: new Date().toISOString() });
  },
  async getSettings() { return (await getDatabase()).get("settings", "primary"); },
  async saveSettings(settings: SiteSettings) { await (await getDatabase()).put("settings", settings); },
  async listMedia(projectId?: string) {
    const db = await getDatabase();
    return projectId ? db.getAllFromIndex("media", "by-project", projectId) : db.getAll("media");
  },
  async saveMedia(record: MediaRecord) { await (await getDatabase()).put("media", record); },
  async removeMedia(id: string) { await (await getDatabase()).delete("media", id); },
  async replaceAll(projects: Project[], media: MediaRecord[], settings: SiteSettings) {
    const db = await getDatabase();
    const tx = db.transaction(["projects", "media", "settings"], "readwrite");
    await tx.objectStore("projects").clear(); await tx.objectStore("media").clear();
    for (const row of projects) await tx.objectStore("projects").put(row);
    for (const row of media) await tx.objectStore("media").put(row);
    await tx.objectStore("settings").put(settings); await tx.done;
  },
};

export type { MediaRecord };
